import React, { useEffect, useState } from 'react';
import {
  batch, shallowEqual, useDispatch, useSelector
} from 'react-redux';
import { DateTime } from 'luxon';

import { ReactComponent as IconFilter } from '../../../../assets/icon/ic_filter.svg';
import ButtonTypes from '../../../../constants/buttonTypes';
import Text from '../../../../components/Text/Text';
import CustomSelect from '../../../../components/Select/CustomSelect';
import Button from '../../../../components/Button/Button';
import CustomDropdownMenu from '../../../../components/CustomDropdownMenu/CustomDropdownMenu';
import { setCoOrdersFilter } from '../../../../actions/coilovers/orders/coOrdersFilter';
import getCoOrders from '../../../../thunks/coilovers/orders/getCoOrders';
import { coOrderStatusList } from '../../../../constants/coOrderStatus';
import { coOrderAssemblyStyleList, coOrderAssemblyTypeList } from '../../../../constants/coOrderAssemblyType';
import { IconReload } from '../../../../assets/icon';
import Input from '../../../../components/Input/Input';
import CustomDatePicker from '../../../../components/CustomDatePicker/CustomDatePicker';
import getOrderDoneByList from '../../../../thunks/general/getOrderDoneByList';
import PermissionsGate from '../../../../components/PermissionsGate/PermissionsGate';
import { permissions } from '../../../../common/permissionGroup';

const selectContainerClassName = 'max-w-[350px] mb-[20px]';
const selectRowClassName = 'flex flex-row gap-[12px]';
const selectHalfClassName = 'flex-1 min-w-[240px]';
const labelContainerClassName = 'mb-[6px]';
const labelTextClassName = 'text-[13px]';
const inputDividerClassName = 'mx-[10px]';
const inputClassName = 'py-[8px] px-[13px] text-[13px]';
const buttonClassName = 'mr-[10px]';

const CoOrderRightHeader = () => {
  const dispatch = useDispatch();
  const [coOrderUserSelected, setCoOrderUserSelected] = useState([]);
  const [coOrderStatusSelected, setCoOrderStatusSelected] = useState([]);
  const [coOrderAssemblyTypeSelected, setCoOrderAssemblyTypeSelected] = useState([]);
  const [coOrderAssemblyStyleSelected, setCoOrderAssemblyStyleSelected] = useState([]);
  const [coOrderAssemblyFromId, setCoOrderAssemblyFromId] = useState(undefined);
  const [coOrderAssemblyToId, setCoOrderAssemblyToId] = useState(undefined);
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const [assembledStartDate, setAssembledStartDate] = useState();
  const [assembledEndDate, setAssembledEndDate] = useState();
  const [menuVisible, setMenuVisible] = useState(false);

  const {
    coOrdersFilter: {
      orderStatus,
      orderAssemblyType,
      orderAssemblyStyle,
      fromId,
      toId,
      marketplaceDateFrom,
      marketplaceDateTo,
      assembledDateTo,
      assembledDateFrom,
    },
    coOrdersFilter,
    getOrderDoneByList: {
      data: orderDoneByListData,
      loading: doneUserLoading
    }
  } = useSelector((state) => ({
    coOrdersFilter: state?.coOrdersFilter,
    getOrderDoneByList: state?.getOrderDoneByList,
  }), shallowEqual);

  useEffect(() => {
    if (menuVisible) {
      dispatch(getOrderDoneByList());
    }
  }, [menuVisible]);

  useEffect(() => {
    if (orderStatus) {
      setCoOrderStatusSelected(orderStatus.map((item) => (
        { label: item, value: item }
      )));
    }
    if (orderAssemblyType) {
      setCoOrderAssemblyTypeSelected(orderAssemblyType.map((item) => (
        { label: item, value: item }
      )));
    }
    if (orderAssemblyStyle) {
      setCoOrderAssemblyStyleSelected(orderAssemblyStyle.map((item) => (
        { label: item, value: item }
      )));
    }
    if (fromId) {
      setCoOrderAssemblyFromId(fromId);
    }
    if (toId) {
      setCoOrderAssemblyToId(toId);
    }
    if (marketplaceDateFrom) {
      setStartDate(new Date(`${marketplaceDateFrom}T00:00:00`));
    }
    if (marketplaceDateTo) {
      setEndDate(new Date(`${marketplaceDateTo}T00:00:00`));
    }

    if (assembledDateFrom) {
      setAssembledStartDate(new Date(`${assembledDateFrom}T00:00:00`));
    }
    if (assembledDateTo) {
      setAssembledEndDate(new Date(`${assembledDateTo}T00:00:00`));
    }
  }, [coOrdersFilter]);

  const onReset = (withSearch) => {
    setCoOrderStatusSelected([]);
    setCoOrderAssemblyTypeSelected([]);
    setCoOrderAssemblyStyleSelected([]);
    setCoOrderUserSelected([]);
    setCoOrderAssemblyFromId('');
    setCoOrderAssemblyToId('');
    setStartDate(undefined);
    setEndDate(undefined);
    setAssembledStartDate(undefined);
    setAssembledEndDate(undefined);
    batch(() => {
      dispatch(setCoOrdersFilter({
        orderStatus: false,
        orderAssemblyType: false,
        orderAssemblyStyle: false,
        page: 1,
        order: undefined,
        fromId: undefined,
        toId: undefined,
        marketplaceDateFrom: undefined,
        marketplaceDateTo: undefined,
        assembledDateFrom: undefined,
        assembledDateTo: undefined,
        doneByUser: undefined,
      }));
    });
    if (withSearch) {
      dispatch(getCoOrders());
    }
  };

  const onApply = () => {
    const marketplaceDateFromNew = startDate ? DateTime.fromISO(startDate?.toISOString()).toFormat('yyyy-MM-dd') : '';
    const marketplaceDateToNew = endDate ? DateTime.fromISO(endDate?.toISOString()).toFormat('yyyy-MM-dd') : '';
    const assembledDateFromNew = assembledStartDate
      ? DateTime.fromISO(assembledStartDate?.toISOString()).toFormat('yyyy-MM-dd')
      : '';
    const assembledDateToNew = assembledEndDate
      ? DateTime.fromISO(assembledEndDate?.toISOString()).toFormat('yyyy-MM-dd')
      : '';

    batch(() => {
      dispatch(setCoOrdersFilter({
        ...(coOrderStatusSelected && { orderStatus: coOrderStatusSelected.map(({ value }) => value) }),
        ...(coOrderAssemblyTypeSelected && {
          orderAssemblyType: coOrderAssemblyTypeSelected.map(({ value }) => value)
        }),
        ...(coOrderAssemblyStyleSelected && {
          orderAssemblyStyle: coOrderAssemblyStyleSelected.map(({ value }) => value)
        }),
        ...(coOrderUserSelected && {
          doneByUser: coOrderUserSelected.map(({ value }) => value)
        }),
        fromId: coOrderAssemblyFromId || undefined,
        toId: coOrderAssemblyToId || undefined,
        marketplaceDateFrom: marketplaceDateFromNew || undefined,
        marketplaceDateTo: marketplaceDateToNew || undefined,
        assembledDateTo: assembledDateToNew || undefined,
        assembledDateFrom: assembledDateFromNew || undefined,
        page: 1
      }));
      dispatch(getCoOrders());
    });
    setMenuVisible(false);
  };

  return (
    <div className="flex flex-row items-center">
      {(!!coOrderStatusSelected.length || !!coOrderAssemblyTypeSelected.length || !!coOrderAssemblyStyleSelected.length)
        && (
        <Button
          icon={<IconReload className="w-[14px] h-[14px]" />}
          type={ButtonTypes.PRIMARY}
          onClick={() => onReset(true)}
          tooltipId="resetFilter"
          tooltip="Reset filter"
          containerStyle="mr-[8px] p-0 w-[30px] h-[30px] min-w-[30px] min-h-[30px] shadow-sm"
        />
        )}
      <CustomDropdownMenu
        Icon={IconFilter}
        btnText="Filter"
        btnType={ButtonTypes.PRIMARY}
        headerText="Filter Options"
        show={menuVisible}
        onToggle={setMenuVisible}
        indicator={!!coOrderStatusSelected.length
          || !!coOrderAssemblyTypeSelected.length
          || !!coOrderAssemblyStyleSelected.length}
      >
        <div className={selectRowClassName}>
          <div className={`${selectContainerClassName} ${selectHalfClassName}`}>
            <Text text="Order status:" styleContainer={labelContainerClassName} textStyle={labelTextClassName} />
            <CustomSelect
              value={coOrderStatusSelected}
              options={coOrderStatusList}
              isClearable
              isMulti
              onChange={setCoOrderStatusSelected}
            />
          </div>
          <div className={`${selectContainerClassName} ${selectHalfClassName}`}>
            <Text text="Type:" styleContainer={labelContainerClassName} textStyle={labelTextClassName} />
            <CustomSelect
              value={coOrderAssemblyTypeSelected}
              options={coOrderAssemblyTypeList}
              isClearable
              isMulti
              onChange={setCoOrderAssemblyTypeSelected}
            />
          </div>
        </div>
        <PermissionsGate can={permissions.ADMIN_ADMIN}>
          <div className={selectRowClassName}>
            <div className={`${selectContainerClassName} ${selectHalfClassName}`}>
              <Text text="Style:" styleContainer={labelContainerClassName} textStyle={labelTextClassName} />
              <CustomSelect
                value={coOrderAssemblyStyleSelected}
                options={coOrderAssemblyStyleList}
                isClearable
                isMulti
                onChange={setCoOrderAssemblyStyleSelected}
              />
            </div>
            <div className={`${selectContainerClassName} ${selectHalfClassName}`}>
              <Text text="Assemblers:" styleContainer={labelContainerClassName} textStyle={labelTextClassName} />
              <CustomSelect
                value={coOrderUserSelected}
                options={orderDoneByListData}
                isClearable
                isMulti
                onChange={setCoOrderUserSelected}
                isLoading={doneUserLoading}
              />
            </div>
          </div>
          <div className="flex flex-row mt-[8px]">
            <div className="flex flex-col flex-1">
              <Text
                text="Market date from:"
                styleContainer={labelContainerClassName}
                textStyle={labelTextClassName}
              />
              <CustomDatePicker
                startDate={startDate}
                setStartDate={setStartDate}
                selectsRange={false}
                maxDate={new Date()}
              />
            </div>
            <div className={inputDividerClassName} />
            <div className="flex flex-col flex-1">
              <Text
                text="Market date to:"
                styleContainer={labelContainerClassName}
                textStyle={labelTextClassName}
              />
              <CustomDatePicker
                startDate={endDate}
                setStartDate={setEndDate}
                selectsRange={false}
                maxDate={new Date()}
              />
            </div>
          </div>
          <div className="flex flex-row">
            <div className="flex flex-col flex-1">
              <Text
                text="Assembled date from:"
                styleContainer={labelContainerClassName}
                textStyle={labelTextClassName}
              />
              <CustomDatePicker
                startDate={assembledStartDate}
                setStartDate={setAssembledStartDate}
                selectsRange={false}
                maxDate={new Date()}
              />
            </div>
            <div className={inputDividerClassName} />
            <div className="flex flex-col flex-1">
              <Text
                text="Assembled date to:"
                styleContainer={labelContainerClassName}
                textStyle={labelTextClassName}
              />
              <CustomDatePicker
                startDate={assembledEndDate}
                setStartDate={setAssembledEndDate}
                selectsRange={false}
                maxDate={new Date()}
              />
            </div>
          </div>
          <div className="w-full">
            <Text text="ID range:" styleContainer={labelContainerClassName} textStyle={labelTextClassName} />
            <div className="flex flex-row gap-[10px] w-full">
              <Input
                type="number"
                onChange={({ target: { value } }) => setCoOrderAssemblyFromId(value || 0)}
                placeholder="from"
                min={1}
                inputStyle={inputClassName}
                value={coOrderAssemblyFromId || ''}
                containerStyle="w-full"
              />
              <Input
                type="number"
                onChange={({ target: { value } }) => setCoOrderAssemblyToId(value)}
                placeholder="to"
                inputStyle={inputClassName}
                value={coOrderAssemblyToId || ''}
                containerStyle="w-full"
              />
            </div>
          </div>
        </PermissionsGate>
        <div className="flex flex-row justify-end mb-[8px] mt-[8px]">
          <div className="flex flex-row">
            <Button
              text="Reset"
              ype={ButtonTypes.ACTIVE_LIGHT}
              containerStyle={buttonClassName}
              onClick={onReset}
            />
            <Button text="Apply" onClick={onApply} />
          </div>
        </div>
      </CustomDropdownMenu>
    </div>
  );
};

CoOrderRightHeader.propTypes = {};

CoOrderRightHeader.defaultProps = {};

export default CoOrderRightHeader;
